/**
 * Filter Helper Functions
 *
 * These functions resolve filter IDs from URL params to full objects with names
 * for display in the UI.
 */

/**
 * Resolve supplier IDs to supplier objects with names
 * @param {number[]} supplier_ids - Array of supplier IDs from URL
 * @param {Object[]} suppliers - Array of supplier objects from category
 * @returns {Object[]} Array of matched supplier objects
 */
export function resolveSuppliers(supplier_ids, suppliers) {
    if ( ! supplier_ids?.length || ! suppliers?.length) return []
    return supplier_ids
        .map(id => suppliers.find(s => s.supplier_id === id))
        .filter(Boolean)
}

/**
 * Resolve designer IDs to designer objects with names
 * @param {number[]} designer_ids - Array of designer IDs from URL
 * @param {Object[]} designers - Array of designer objects from category
 * @returns {Object[]} Array of matched designer objects
 */
export function resolveDesigners(designer_ids, designers) {
    if ( ! designer_ids?.length || ! designers?.length) return []
    return designer_ids
        .map(id => designers.find(d => d.designer_id === id))
        .filter(Boolean)
}

/**
 * Resolve tag IDs to tag objects with names
 * Tags are nested inside tag groups, so we need to search across all groups
 * @param {number[]} tag_ids - Array of tag IDs from URL
 * @param {Object[]} tagGroups - Array of tag group objects, each containing a tags array
 * @returns {Object[]} Array of matched tag objects
 */
export function resolveTags(tag_ids, tagGroups) {
    if ( ! tag_ids?.length || ! tagGroups?.length) return []

    // Flatten all tags from all groups
    const allTags = tagGroups.flatMap(group => group.tags || [])

    return tag_ids
        .map(id => allTags.find(t => t.tag_id === id))
        .filter(Boolean)
}

/**
 * Format price filter for display
 * @param {number|null} min_price - Minimum price from URL
 * @param {number|null} max_price - Maximum price from URL
 * @returns {Object|null} Formatted price filter object or null if no price filter
 */
export function formatPriceFilter(min_price, max_price) {
    if ( ! min_price && ! max_price) return null

    let label = ''
    if (min_price && max_price) {
        label = `$${min_price.toLocaleString()} - $${max_price.toLocaleString()}`
    } else if (min_price) {
        label = `$${min_price.toLocaleString()} or more`
    } else if (max_price) {
        label = `Up to $${max_price.toLocaleString()}`
    }

    return {
        min: min_price,
        max: max_price,
        label,
    }
}

/**
 * Format sort option for display
 * @param {string} sort_key - Sort key from URL
 * @returns {Object|null} Formatted sort object or null if no sort
 */
export function formatSortLabel(sort_key) {
    if ( ! sort_key) return null

    const sortLabels = {
        'best_seller': 'Best Seller',
        'most_popular': 'Best Seller', // backward compatibility
        'price_low_to_high': 'Price: Low to High',
        'price_high_to_low': 'Price: High to Low',
        'recently_added': 'Recently Added',
        'designer': 'Designer',
        'supplier': 'Brand',
    }

    return {
        key: sort_key,
        label: sortLabels[sort_key] || sort_key,
    }
}

/**
 * Parse all filters from URL hash
 * Hash format: #suppliers=1,2&designers=3&tags=4,5&min_price=100&max_price=500&sort_by=price_low_to_high
 * @param {string} hash - window.location.hash string (with or without leading #)
 * @returns {Object} Parsed filter values (IDs and raw values)
 */
export function parseFiltersFromHash(hash) {
    // Remove leading # if present
    const hashString = hash?.startsWith('#') ? hash.slice(1) : (hash || '')
    const params = new URLSearchParams(hashString)

    return {
        supplier_ids: params.get('suppliers')?.split(',').map(Number).filter(Boolean) || [],
        designer_ids: params.get('designers')?.split(',').map(Number).filter(Boolean) || [],
        tag_ids: params.get('tags')?.split(',').map(Number).filter(Boolean) || [],
        min_price: params.get('min_price') ? Number(params.get('min_price')) : null,
        max_price: params.get('max_price') ? Number(params.get('max_price')) : null,
        sort_by: params.get('sort_by') || null,
    }
}

/**
 * Build hash string from filter values
 * @param {Object} filters - Filter values object
 * @returns {string} Hash string (without leading #)
 */
export function buildHashFromFilters(filters) {
    const params = new URLSearchParams()

    if (filters.supplier_ids?.length > 0) {
        params.set('suppliers', filters.supplier_ids.join(','))
    }
    if (filters.designer_ids?.length > 0) {
        params.set('designers', filters.designer_ids.join(','))
    }
    if (filters.tag_ids?.length > 0) {
        params.set('tags', filters.tag_ids.join(','))
    }
    if (filters.min_price !== null && filters.min_price !== undefined) {
        params.set('min_price', filters.min_price)
    }
    if (filters.max_price !== null && filters.max_price !== undefined) {
        params.set('max_price', filters.max_price)
    }
    if (filters.sort_by) {
        params.set('sort_by', filters.sort_by)
    }

    return params.toString()
}

/**
 * Check if any filters are active
 * @param {Object} filters - Parsed filters object from parseFiltersFromUrl
 * @returns {boolean} True if any filter is active
 */
export function hasActiveFilters(filters) {
    return (
        filters.supplier_ids?.length > 0 ||
        filters.designer_ids?.length > 0 ||
        filters.tag_ids?.length > 0 ||
        filters.min_price ||
        filters.max_price ||
        filters.sort_by
    )
}
